/**
 * Per-domain cache of the scan-derived blocking manifest.
 *
 * The manifest (the consent-blocklist edge function) lists trackers the
 * scanner found on the customer's site that the static patterns in
 * ScriptBlocker do not know. Fetching it on every page load cannot work on
 * its own: by the time the response arrives the parser has run every script
 * above and around the embed, so a tracker known only to the scanner has
 * already executed.
 *
 * So the last manifest is kept in localStorage and applied synchronously,
 * before intercept(), on the next page load. The network copy is refreshed in
 * the background when the cached one is older than REFRESH_AFTER_MS. The very
 * first page view uses an inline data-pb-blocklist snapshot when supplied by
 * Deploy. Without it, scan-only rules still depend on the network on a fresh
 * browser. Refreshed rules apply to scripts inserted after they arrive.
 *
 * The entry holds only URL patterns and category names from the customer's
 * own scan results: no visitor data. It exists solely to operate the consent
 * mechanism, like pb_consent.
 */

import type { BlockPattern, ConsentCategory } from "./ScriptBlocker";

export interface BlocklistEntry {
  pattern: string;
  category: string;
}

interface CachedBlocklist {
  patterns: BlocklistEntry[];
  fetchedAt: number;
}

/** Refresh the cached manifest in the background once it is this old. */
export const REFRESH_AFTER_MS = 60 * 60 * 1000;
/**
 * Stop applying a cached manifest this old. Over-blocking a script the scanner
 * once classified as a tracker is the safe direction, so this is generous; it
 * only exists so a domain that stopped embedding Beacon does not carry the
 * entry forever.
 */
export const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const VALID_CATEGORIES = new Set<ConsentCategory>(["analytics", "advertising", "functional", "social"]);
const MAX_ENTRIES = 500;
const MAX_PATTERN_LENGTH = 512;

export function blocklistCacheKey(domainId: string): string {
  return `pb_bl_${domainId}`;
}

/** Keep only well-formed entries in a blockable category. */
export function sanitizeBlocklist(entries: unknown): BlocklistEntry[] {
  if (!Array.isArray(entries)) return [];
  const out: BlocklistEntry[] = [];
  for (const entry of entries) {
    if (out.length >= MAX_ENTRIES) break;
    if (!entry || typeof entry !== "object") continue;
    const { pattern, category } = entry as { pattern?: unknown; category?: unknown };
    if (typeof pattern !== "string" || typeof category !== "string") continue;
    const trimmed = pattern.trim();
    if (!trimmed || trimmed.length > MAX_PATTERN_LENGTH) continue;
    if (!VALID_CATEGORIES.has(category as ConsentCategory)) continue;
    out.push({ pattern: trimmed, category });
  }
  return out;
}

/** Turn manifest entries into literal, case-insensitive substring matchers. */
export function compileBlocklist(entries: BlocklistEntry[]): BlockPattern[] {
  return entries.map((entry) => ({
    pattern: new RegExp(entry.pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i"),
    category: entry.category as ConsentCategory,
  }));
}

/**
 * Read the cached manifest. Returns the compiled patterns (empty when there is
 * no usable entry) and whether a background refresh is due.
 */
export function readCachedBlocklist(domainId: string, now: number = Date.now()): { patterns: BlockPattern[]; stale: boolean } {
  try {
    const raw = localStorage.getItem(blocklistCacheKey(domainId));
    if (!raw) return { patterns: [], stale: true };
    const parsed = JSON.parse(raw) as Partial<CachedBlocklist>;
    const fetchedAt = typeof parsed.fetchedAt === "number" ? parsed.fetchedAt : 0;
    const age = now - fetchedAt;
    if (!(age >= 0) || age > MAX_AGE_MS) return { patterns: [], stale: true };
    return {
      patterns: compileBlocklist(sanitizeBlocklist(parsed.patterns)),
      stale: age > REFRESH_AFTER_MS,
    };
  } catch {
    return { patterns: [], stale: true };
  }
}

export function writeCachedBlocklist(domainId: string, entries: BlocklistEntry[], now: number = Date.now()): void {
  try {
    const value: CachedBlocklist = { patterns: sanitizeBlocklist(entries), fetchedAt: now };
    localStorage.setItem(blocklistCacheKey(domainId), JSON.stringify(value));
  } catch {
    // Storage unavailable (private mode, quota): the network copy still
    // applies for scripts inserted after it arrives.
  }
}
